import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ToastService, AngularToastifyModule } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { VatSettingService } from '../../../../../core/services/vat-setting.service';
import { addVatToExclusive } from '../../../../../core/utils/vat-amount.util';

interface MockInvoice {
  id: number;
  invoiceNo: string;
  customerName: string;
  fee: number;
  vat: number;
  status: number; // 1: Waiting, 2: Paid, 3: Cancelled, 4: Overdue
  invoiceDate: Date;
  dueDate: Date;
  address: string;
  taxId: string;
  branchNo: string;
}

@Component({
  selector: 'app-invoice-test',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    AngularToastifyModule,
    PssLoadingComponent
  ],
  templateUrl: './invoice-test.component.html',
  styleUrls: ['./invoice-test.component.css']
})
export class InvoiceTestComponent implements OnInit {
  // ================= State Variables =================
  isLoading: boolean = false;
  showDashboard: boolean = true;
  showTableFilters: boolean = false;

  // Search & Basic Filters
  searchText: string = '';
  searchType: 'customerName' | 'invoiceNo' = 'customerName';
  isSearchTypeDropdownOpen: boolean = false;

  // Advanced Filters
  filterStatus: number | 'all' = 'all';
  startDate: Date | null = null;
  endDate: Date | null = null;

  // Customer Autocomplete Dropdown
  searchFieldText: string = '';
  selectedCustomerFilter: string = 'all';
  isCustomerDropdownOpen: boolean = false;
  customerOptions: string[] = [
    'บริษัท ปตท. จำกัด (มหาชน)',
    'บริษัท ปูนซิเมนต์ไทย จำกัด (มหาชน)',
    'บริษัท แอดวานซ์ อินโฟร์ เซอร์วิส จำกัด (มหาชน)',
    'บริษัท ซีพี ออลล์ จำกัด (มหาชน)',
    'บริษัท ท่าอากาศยานไทย จำกัด (มหาชน)'
  ];

  // Pagination
  currentPage: number = 1;
  pageSize: number = 5;
  pageSizeOptions: number[] = [5, 10, 20, 50];

  // Sorting
  sortColumn: string = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Interactive Action States
  isConfirmModalOpen: boolean = false;
  selectedInvoiceForCancel: MockInvoice | null = null;
  isProcessingAction: boolean = false;

  // Chart States
  chartMode: 'daily' | 'monthly' = 'daily';
  chartType: 'line' | 'bar' = 'line';
  yAxisLabels: number[] = [];
  chartTotalCount: number = 0;
  chartDots: any[] = [];
  chartPathStr: string = '';
  chartAreaPathStr: string = '';

  // Invoices Array (Initial Mock Data)
  invoices: MockInvoice[] = [
    {
      id: 1001,
      invoiceNo: 'INV-2026-001',
      customerName: 'บริษัท ปตท. จำกัด (มหาชน)',
      fee: 25000,
      vat: 1750,
      status: 2,
      invoiceDate: new Date('2026-06-01'),
      dueDate: new Date('2026-06-15'),
      address: '555 ถนนวิภาวดีรังสิต แขวงจตุจักร เขตจตุจักร กรุงเทพฯ 10900',
      taxId: '0107535000165',
      branchNo: '00000'
    },
    {
      id: 1002,
      invoiceNo: 'INV-2026-002',
      customerName: 'บริษัท ปูนซิเมนต์ไทย จำกัด (มหาชน)',
      fee: 18000,
      vat: 1260,
      status: 1,
      invoiceDate: new Date('2026-06-05'),
      dueDate: new Date('2026-06-20'),
      address: '1 ถนนปูนซิเมนต์ไทย แขวงบางซื่อ เขตบางซื่อ กรุงเทพฯ 10800',
      taxId: '0107537000042',
      branchNo: '00001'
    },
    {
      id: 1003,
      invoiceNo: 'INV-2026-003',
      customerName: 'บริษัท แอดวานซ์ อินโฟร์ เซอร์วิส จำกัด (มหาชน)',
      fee: 45000,
      vat: 3150,
      status: 1,
      invoiceDate: new Date('2026-06-10'),
      dueDate: new Date('2026-06-25'),
      address: '414 ถนนพหลโยธิน แขวงสามเสนใน เขตพญาไท กรุงเทพฯ 10400',
      taxId: '0107535000265',
      branchNo: '00000'
    },
    {
      id: 1004,
      invoiceNo: 'INV-2026-004',
      customerName: 'บริษัท ซีพี ออลล์ จำกัด (มหาชน)',
      fee: 8500,
      vat: 595,
      status: 4,
      invoiceDate: new Date('2026-05-15'),
      dueDate: new Date('2026-05-30'),
      address: '313 อาคาร ซี.พี.ทาวเวอร์ ถนนสีลม แขวงสีลม เขตบางรัก กรุงเทพฯ 10500',
      taxId: '0107542000011',
      branchNo: '00003'
    },
    {
      id: 1005,
      invoiceNo: 'INV-2026-005',
      customerName: 'บริษัท ท่าอากาศยานไทย จำกัด (มหาชน)',
      fee: 62000,
      vat: 4340,
      status: 3,
      invoiceDate: new Date('2026-06-12'),
      dueDate: new Date('2026-06-27'),
      address: '333 ถนนเชิดวุฒากาศ แขวงสีกัน เขตดอนเมือง กรุงเทพฯ 10210',
      taxId: '0107545000292',
      branchNo: '00000'
    }
  ];

  private readonly vatSetting = inject(VatSettingService);

  constructor(private toast: ToastService) { }

  ngOnInit() {
    this.setDefaultDates();
    this.processChartData();
  }

  // ================= Normal Functions =================

  /**
   * กำหนดช่วงเวลาเริ่มต้นให้เป็น 30 วันที่ผ่านมา
   */
  setDefaultDates() {
    const today = new Date();
    this.endDate = today;
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    this.startDate = thirtyDaysAgo;
  }

  /**
   * เปิด/ปิดแดชบอร์ดด้านบนแบบมีแอนิเมชันพับ
   */
  toggleDashboard() {
    this.showDashboard = !this.showDashboard;
  }

  /**
   * เปิด/ปิดแผงตัวกรองขั้นสูงด้านล่าง
   */
  toggleTableFilters() {
    this.showTableFilters = !this.showTableFilters;
  }

  /**
   * สลับโหมดการค้นหา (ชื่อลูกค้า / เลขที่ใบแจ้งหนี้)
   */
  toggleSearchTypeDropdown() {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  selectSearchType(type: 'customerName' | 'invoiceNo') {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  get currentSearchTypeLabel(): string {
    return this.searchType === 'customerName' ? 'ชื่อลูกค้า' : 'เลขที่ใบแจ้งหนี้';
  }

  onSearchChange() {
    this.currentPage = 1;
    this.processChartData();
  }

  onFilterChange() {
    this.currentPage = 1;
    this.processChartData();
  }

  onDateChange() {
    this.currentPage = 1;
    this.processChartData();
  }

  onRefresh() {
    this.searchText = '';
    this.searchType = 'customerName';
    this.filterStatus = 'all';
    this.selectedCustomerFilter = 'all';
    this.searchFieldText = '';
    this.setDefaultDates();
    this.currentPage = 1;
    this.processChartData();
    this.toast.info('รีเฟรชข้อมูลเรียบร้อยแล้ว');
  }

  // --- Customer Dropdown / Combo Box Autocomplete Logic ---

  get filteredOptions(): string[] {
    const query = this.searchFieldText.toLowerCase().trim();
    if (!query) return this.customerOptions;
    return this.customerOptions.filter(opt => opt.toLowerCase().includes(query));
  }

  toggleCustomerDropdown() {
    this.isCustomerDropdownOpen = !this.isCustomerDropdownOpen;
    if (this.isCustomerDropdownOpen && this.selectedCustomerFilter === 'all') {
      this.searchFieldText = '';
    }
  }

  selectCustomerOption(customer: string) {
    this.selectedCustomerFilter = customer;
    this.searchFieldText = customer;
    this.isCustomerDropdownOpen = false;
    this.onFilterChange();
  }

  clearCustomerSelection() {
    this.selectedCustomerFilter = 'all';
    this.searchFieldText = '';
    this.isCustomerDropdownOpen = false;
    this.onFilterChange();
  }

  closeDropdowns() {
    setTimeout(() => {
      this.isCustomerDropdownOpen = false;
      const match = this.customerOptions.find(opt => opt.toLowerCase() === this.searchFieldText.toLowerCase().trim());
      if (match) {
        this.selectedCustomerFilter = match;
        this.searchFieldText = match;
      } else {
        if (this.selectedCustomerFilter !== 'all') {
          this.searchFieldText = this.selectedCustomerFilter;
        } else {
          this.searchFieldText = '';
        }
      }
      this.onFilterChange();
    }, 200);
  }

  // --- Data Calculations for KPIs ---

  get totalAmount(): number {
    return this.filteredData.reduce((sum, inv) => sum + (inv.fee + inv.vat), 0);
  }

  get paidAmount(): number {
    return this.filteredData
      .filter(inv => inv.status === 2)
      .reduce((sum, inv) => sum + (inv.fee + inv.vat), 0);
  }

  get pendingAmount(): number {
    return this.filteredData
      .filter(inv => inv.status === 1)
      .reduce((sum, inv) => sum + (inv.fee + inv.vat), 0);
  }

  get overdueAmount(): number {
    return this.filteredData
      .filter(inv => inv.status === 4)
      .reduce((sum, inv) => sum + (inv.fee + inv.vat), 0);
  }

  get paidRatioPercent(): number {
    const total = this.totalAmount;
    if (total === 0) return 0;
    return Math.round((this.paidAmount / total) * 100);
  }

  // --- Table Sorting & Pagination ---

  sortData(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
  }

  setPage(page: number) {
    const max = this.totalPages;
    if (page >= 1 && page <= max) {
      this.currentPage = page;
    }
  }

  onPageSizeChange() {
    this.currentPage = 1;
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get totalItems(): number {
    return this.filteredData.length;
  }

  get totalPages(): number {
    if (this.pageSize === 0) return 0;
    return Math.ceil(this.totalItems / this.pageSize);
  }

  get pagesArray(): number[] {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  // --- Filter and Search logic (combining both standard and advanced filter) ---

  get filteredData(): MockInvoice[] {
    let data = [...this.invoices];

    // 1. Search Box with Search Type
    if (this.searchText) {
      const search = this.searchText.toLowerCase().trim();
      data = data.filter(inv => {
        if (this.searchType === 'customerName') {
          return (inv.customerName || '').toLowerCase().includes(search);
        } else {
          return (inv.invoiceNo || '').toLowerCase().includes(search);
        }
      });
    }

    // 2. Autocomplete Filter Customer
    if (this.selectedCustomerFilter !== 'all') {
      data = data.filter(inv => inv.customerName === this.selectedCustomerFilter);
    }

    // 3. Status Filter
    if (this.filterStatus !== 'all') {
      data = data.filter(inv => inv.status === Number(this.filterStatus));
    }

    // 4. Date Range filter
    if (this.startDate) {
      const start = new Date(this.startDate);
      start.setHours(0, 0, 0, 0);
      data = data.filter(inv => new Date(inv.invoiceDate) >= start);
    }
    if (this.endDate) {
      const end = new Date(this.endDate);
      end.setHours(23, 59, 59, 999);
      data = data.filter(inv => new Date(inv.invoiceDate) <= end);
    }

    // 5. Sorting
    data.sort((a: any, b: any) => {
      let valA = a[this.sortColumn];
      let valB = b[this.sortColumn];

      if (this.sortColumn === 'total') {
        valA = a.fee + a.vat;
        valB = b.fee + b.vat;
      }

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return this.sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return this.sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }

  get paginatedData(): MockInvoice[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }

  // --- Display Formatting Helpers ---

  getStatusConfig(status: number) {
    switch (status) {
      case 1:
        return {
          label: 'รอชำระ',
          class: 'bg-amber-50 text-amber-600 border-amber-200/50',
          icon: 'hourglass_top',
          iconClass: 'text-amber-500 animate-pulse'
        };
      case 2:
        return {
          label: 'ชำระแล้ว',
          class: 'bg-emerald-50 text-emerald-600 border-emerald-200/50',
          icon: 'check_circle',
          iconClass: 'text-emerald-500'
        };
      case 3:
        return {
          label: 'ยกเลิก',
          class: 'bg-rose-50 text-rose-600 border-rose-200/50',
          icon: 'cancel',
          iconClass: 'text-rose-500'
        };
      case 4:
        return {
          label: 'เกินกำหนด',
          class: 'bg-red-50 text-red-600 border-red-200/50',
          icon: 'event_busy',
          iconClass: 'text-red-500'
        };
      default:
        return {
          label: 'ไม่ระบุ',
          class: 'bg-slate-50 text-slate-600 border-slate-200/50',
          icon: 'help_outline',
          iconClass: 'text-slate-400'
        };
    }
  }

  formatDateDisplay(date: Date): string {
    if (!date) return '-';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear() + 543; // Buddhist Era
    return `${day}/${month}/${year}`;
  }

  // --- Chart Processing logic ---

  setChartMode(mode: 'daily' | 'monthly') {
    this.chartMode = mode;
    this.processChartData();
  }

  setChartType(type: 'line' | 'bar') {
    this.chartType = type;
    this.processChartData();
  }

  processChartData() {
    const points: { label: string; count: number; date: Date }[] = [];
    const dateMap = new Map<string, number>();

    if (this.chartMode === 'daily') {
      const today = new Date();
      // Generate past 7 days
      for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        const key = d.toDateString();
        dateMap.set(key, 0);
        points.push({
          label: `${d.getDate()} ${d.toLocaleDateString('th-TH', { month: 'short' })}`,
          count: 0,
          date: d
        });
      }

      this.filteredData.forEach(inv => {
        const key = new Date(inv.invoiceDate).toDateString();
        if (dateMap.has(key)) {
          dateMap.set(key, (dateMap.get(key) || 0) + 1);
        }
      });

      points.forEach(p => {
        p.count = dateMap.get(p.date.toDateString()) || 0;
      });
    } else {
      // Monthly past 6 months
      const today = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        dateMap.set(key, 0);
        points.push({
          label: d.toLocaleDateString('th-TH', { month: 'short' }),
          count: 0,
          date: d
        });
      }

      this.filteredData.forEach(inv => {
        const d = new Date(inv.invoiceDate);
        const key = `${d.getFullYear()}-${d.getMonth()}`;
        if (dateMap.has(key)) {
          dateMap.set(key, (dateMap.get(key) || 0) + 1);
        }
      });

      points.forEach(p => {
        p.count = dateMap.get(`${p.date.getFullYear()}-${p.date.getMonth()}`) || 0;
      });
    }

    this.chartTotalCount = points.reduce((sum, item) => sum + item.count, 0);
    const maxVal = Math.max(...points.map(p => p.count), 5);
    const roundedMax = Math.ceil(maxVal / 4) * 4;
    this.yAxisLabels = [0, roundedMax * 0.25, roundedMax * 0.5, roundedMax * 0.75, roundedMax];

    // Build SVG coordinate points (X: 5% to 95%, Y: 10% to 90%)
    const coords = points.map((p, index) => {
      const x = 5 + (index / (points.length - 1)) * 90;
      const y = 90 - (p.count / roundedMax) * 80;
      return { x, y, label: p.label, value: p.count };
    });

    if (coords.length > 0) {
      // Calculate cubic bezier curves
      let path = `M ${coords[0].x},${coords[0].y}`;
      for (let i = 1; i < coords.length; i++) {
        const prev = coords[i - 1];
        const curr = coords[i];
        const cp1x = prev.x + (curr.x - prev.x) / 3;
        const cp1y = prev.y;
        const cp2x = curr.x - (curr.x - prev.x) / 3;
        const cp2y = curr.y;
        path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
      }
      this.chartPathStr = path;
      this.chartAreaPathStr = `${path} L ${coords[coords.length - 1].x},90 L ${coords[0].x},90 Z`;
    } else {
      this.chartPathStr = '';
      this.chartAreaPathStr = '';
    }
    this.chartDots = coords;
  }

  // ================= API Functions (Simulated) =================

  /**
   * จำลองการเพิ่มใบแจ้งหนี้ใหม่ (Add Mock Invoice)
   */
  addMockInvoice() {
    this.isLoading = true;
    setTimeout(() => {
      const newId = this.invoices.reduce((max, x) => x.id > max ? x.id : max, 1000) + 1;
      const index = Math.floor(Math.random() * this.customerOptions.length);
      const customer = this.customerOptions[index];
      const fee = Math.floor(Math.random() * 50) * 1000 + 5000;
      const vat = addVatToExclusive(fee, this.vatSetting.ratePercent()).vatAmount;
      const statuses = [1, 2, 4]; // Wait, Paid, Overdue
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const invoiceDate = new Date();
      // Random offset within 15 days
      invoiceDate.setDate(invoiceDate.getDate() - Math.floor(Math.random() * 15));
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + 14);

      const newInv: MockInvoice = {
        id: newId,
        invoiceNo: `INV-2026-${String(newId).slice(1)}`,
        customerName: customer,
        fee,
        vat,
        status,
        invoiceDate,
        dueDate,
        address: '123 ถนนเพชรบุรี แขวงมักกะสัน เขตราชเทวี กรุงเทพฯ 10400',
        taxId: '0105560000123',
        branchNo: '00000'
      };

      this.invoices.unshift(newInv);
      this.isLoading = false;
      this.processChartData();
      this.toast.success(`จำลองสร้างใบแจ้งหนี้ ${newInv.invoiceNo} เรียบร้อยแล้ว!`);
    }, 400);
  }

  /**
   * สลับสถานะเพื่อจำลองการปรับปรุงข้อมูล
   */
  toggleInvoiceStatus(invoice: MockInvoice, event: Event) {
    event.stopPropagation();
    // 1 -> 2 -> 4 -> 3 -> 1
    const current = invoice.status;
    let next = 1;
    if (current === 1) next = 2;
    else if (current === 2) next = 4;
    else if (current === 4) next = 3;
    else next = 1;

    invoice.status = next;
    this.processChartData();
    this.toast.info(`เปลี่ยนสถานะของ ${invoice.invoiceNo} เป็น ${this.getStatusConfig(next).label}`);
  }

  /**
   * เปิด Confirm Modal สำหรับการยกเลิกใบแจ้งหนี้
   */
  openCancelConfirm(invoice: MockInvoice, event: Event) {
    event.stopPropagation();
    this.selectedInvoiceForCancel = invoice;
    this.isConfirmModalOpen = true;
  }

  closeCancelConfirm() {
    this.isConfirmModalOpen = false;
    this.selectedInvoiceForCancel = null;
  }

  confirmCancelInvoice() {
    if (!this.selectedInvoiceForCancel) return;
    this.isProcessingAction = true;
    setTimeout(() => {
      this.selectedInvoiceForCancel!.status = 3; // Cancel
      this.isProcessingAction = false;
      this.isConfirmModalOpen = false;
      this.processChartData();
      this.toast.warn(`ยกเลิกใบแจ้งหนี้ ${this.selectedInvoiceForCancel!.invoiceNo} เรียบร้อยแล้ว`);
      this.selectedInvoiceForCancel = null;
    }, 600);
  }
}
